import React, { useEffect, useRef, useState } from 'react';
import { useDataProvider, useRefresh, useNotify } from 'react-admin';
import { Form } from 'react-final-form';
import { FORM_ERROR, FormApi } from 'final-form';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import { makeStyles } from '@material-ui/core';

import { MemberDTO } from 'pages/users/types';
import { Dialog } from 'components/common';
import { Checkbox, Input } from 'components/form';
import { required } from 'components/form/validationRules';

const useStyles = makeStyles(() => ({
  mainRole: {
    fontWeight: 700,
    fontSize: 20,
  },
  rolesContainer: {
    marginTop: '16px',
  },
  roleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
}));

interface Props {
  open: boolean;
  initialValues?: MemberDTO;
  onClose: () => void;
  tenantId: string;
}

interface Role {
  description: string;
  name: string;
}

interface RolesResponse {
  description: string;
  name: string;
  roles: Role[];
}

interface FormValues {
  firstName: string;
  secondName: string;
  phone: string;
  email: string;
  position: string;
  roles: Record<string, boolean>;
}

const defaultValues: FormValues = {
  firstName: '',
  secondName: '',
  phone: '',
  email: '',
  position: '',
  roles: {},
};

const SubUserRolesDialog = ({ open, initialValues, onClose, tenantId }: Props) => {
  const classes = useStyles();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const formRef = useRef<FormApi<FormValues>>();

  const [roles, setRoles] = useState<RolesResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const getRoles = async () => {
      try {
        setLoading(true);
        const { data } = await dataProvider.get('membersRoles', { tenantId });

        setRoles(data);
      } catch (error: any) {
        notify(
          typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
          'warning'
        );
      } finally {
        setLoading(false);
      }
    };

    getRoles();
  }, [dataProvider, notify, tenantId, open]);

  useEffect(() => {
    if (!roles) {
      return;
    }

    if (formRef.current && initialValues) {
      const mappedRoles: Record<string, boolean> = {};
      const filteredMainRoles = roles
        .filter((mainRole) =>
          mainRole.roles.every((role) => initialValues.roles.includes(role.name))
        )
        .map((mainRole) => mainRole.name);

      initialValues.roles?.forEach((role) => {
        mappedRoles[role] = true;
      });
      filteredMainRoles?.forEach((role) => {
        mappedRoles[role] = true;
      });

      const newValues: FormValues = {
        firstName: initialValues.firstName,
        secondName: initialValues.secondName,
        phone: initialValues.phone,
        email: initialValues.email,
        position: initialValues.position,
        roles: mappedRoles,
      };

      formRef.current.reset(newValues);
    }
  }, [initialValues, roles]);

  const handleSubmitForm = async (values) => {
    try {
      const selectedRoles: string[] = [];

      const mainRoles = roles.map((item) => item.name);

      for (const key in values.roles) {
        if (values.roles[key] && !mainRoles.includes(key)) {
          selectedRoles.push(key);
        }
      }

      if (!selectedRoles?.length) {
        return { [FORM_ERROR]: 'Select at least one role' };
      }

      const requestData = {
        ...values,
        roles: selectedRoles,
      };

      let response;

      if (initialValues?.id) {
        response = await dataProvider.update('deleteOrEditMember', {
          tenantId,
          memberId: initialValues?.id,
          data: requestData,
        });
      } else {
        response = await dataProvider.post('createMember', {
          tenantId,
          data: requestData,
        });
      }

      onClose();

      refresh();

      return response;
    } catch (error: any) {
      return { [FORM_ERROR]: error?.message ?? error };
    }
  };

  return (
    <Form<FormValues>
      initialValues={defaultValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, form, values, submitError }) => {
        formRef.current = form;

        return (
          <Dialog
            open={open}
            onCancel={onClose}
            title={initialValues ? 'Edit sub-user' : 'Create sub-user'}
            onConfirm={handleSubmit}
            submitting={submitting}
            error={submitError}
            confirmText="Save">
            <Input name="firstName" label="First name" validate={required} />
            <Input name="secondName" label="Last name" validate={required} />
            <Input
              name="email"
              label="Email"
              validate={required}
              inputProps={{ InputProps: { readOnly: !!initialValues } }}
            />
            <Input name="phone" label="Phone" validate={required} />
            <Input name="position" label="Position" validate={required} />

            <Box className={classes.rolesContainer}>
              {loading ? (
                <CircularProgress />
              ) : (
                roles.map((mainRole) => {
                  const handleToggleMainRole = (value) => {
                    form.change(`roles.${mainRole.name}`, value);
                    mainRole.roles.forEach((role) => form.change(`roles.${role.name}`, value));
                  };

                  return (
                    <Box key={mainRole.name}>
                      <Box className={classes.roleHeader}>
                        <Typography className={classes.mainRole}>
                          {mainRole.description} [{mainRole.name}]
                        </Typography>
                        <Checkbox name={`roles.${mainRole.name}`} onChange={handleToggleMainRole} />
                      </Box>
                      {mainRole.roles.map((role) => {
                        const handleToggleRole = (value) => {
                          form.change(`roles.${role.name}`, value);

                          if (values.roles[mainRole.name] && !value) {
                            form.change(`roles.${mainRole.name}`, false);
                            return;
                          }

                          const updatedRoles = {
                            ...values.roles,
                            [role.name]: value,
                          };

                          const mainRoleSelected = mainRole.roles.every(
                            (role) => updatedRoles[role.name]
                          );

                          if (mainRoleSelected && value) {
                            form.change(`roles.${mainRole.name}`, true);
                          }
                        };

                        return (
                          <Checkbox
                            key={role.name}
                            name={`roles.${role.name}`}
                            label={`${role.description} [${role.name}]`}
                            onChange={handleToggleRole}
                          />
                        );
                      })}
                    </Box>
                  );
                })
              )}
            </Box>
          </Dialog>
        );
      }}
    />
  );
};

export default SubUserRolesDialog;
